import { useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabaseClient";
import { fetchAdminTopTrips, TopTrip } from "../../api/admin";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { Link } from "react-router-dom";

type TimeseriesRow = { bucket_start: string; metric: string; value: number };

const KPI_LABELS: Record<string, string> = {
  users_new: "Neue Nutzer",
  trips_new: "Neue Trips",
  participants_new: "Neue Teilnahme",
  program_items_new: "Programmpunkte",
  documents_new: "Dokumente",
  expenses_new: "Ausgaben",
  payments_new: "Zahlungen",
  expense_shares_new: "Ausgaben-Anteile",
  chat_messages_new: "Chat-Nachrichten",
  disclaimer_acceptances_new: "Disclaimer bestätigt",
  feedback_threads_new: "Feedback-Threads",
  feedback_comments_new: "Feedback-Kommentare",
  feedback_votes_new: "Feedback-Votes",
  todos_new: "Todos",
  todo_assignments_new: "Todo-Zuweisungen",
  quiz_questions_new: "Quiz-Fragen",
  quiz_answers_new: "Quiz-Antworten",
  trip_suggestions_new: "Vorschläge",
  trip_suggestion_votes_new: "Vorschlags-Votes",
  user_packlist_items_new: "Packlisten-Items",
  user_packlist_templates_new: "Packlisten-Templates",
  notifications_new: "Notifications",
  waitlist_requests_new: "Warteliste",
};

const DEFAULT_SELECTED = ["users_new","trips_new","participants_new","program_items_new"] as const;
const COLORS = ["#6366f1","#10b981","#f59e0b","#ef4444","#06b6d4","#8b5cf6","#22c55e","#a855f7","#84cc16","#f97316","#0ea5e9","#64748b","#e11d48","#7c3aed","#9ca3af","#f43f5e","#65a30d","#14b8a6","#0891b2","#4f46e5"];

type CreditStats = {
  total_balance: number;
  users_with_balance: number;
  total_purchased_sc: number;
  total_consumed_sc: number;
  total_plannings: number;
  total_refunded_sc: number;
  purchases_last_7d: number;
  purchases_last_30d: number;
  plannings_last_7d: number;
  plannings_last_30d: number;
};

export default function AdminKpiPage() {
  const [bucket, setBucket] = useState<"day"|"week"|"month">("day");
  const [start, setStart] = useState(() => isoDateNDaysAgo(30));
  const [end, setEnd] = useState(() => isoDateNDaysAgo(0));
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>([...DEFAULT_SELECTED]);
  const [cumulative, setCumulative] = useState(false);

  const [rows, setRows] = useState<TimeseriesRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string|null>(null);

  const [creditStats, setCreditStats] = useState<CreditStats | null>(null);
  const [creditLoading, setCreditLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await (supabase as any).rpc("admin_credit_stats");
      if (data && data.length > 0) setCreditStats(data[0] as CreditStats);
      setCreditLoading(false);
    })();
  }, []);

  useEffect(() => {
    setLoading(true);
    setErr(null);
    (async () => {
      const { data, error } = await supabase.rpc("admin_kpis_timeseries", {
        p_start: start,
        p_end: end,
        p_bucket: bucket,
        // kein p_skip_guard im App-Aufruf → Guard bleibt aktiv
      });
      if (error) setErr(error.message ?? String(error));
      setRows((data ?? []) as TimeseriesRow[]);
      setLoading(false);
    })();
  }, [start, end, bucket]);

  const chartData = useMemo(() => {
    const byDate: Record<string, any> = {};
    for (const r of rows) {
      if (!selectedMetrics.includes(r.metric)) continue;
      byDate[r.bucket_start] ??= { date: r.bucket_start };
      byDate[r.bucket_start][r.metric] = (byDate[r.bucket_start][r.metric] ?? 0) + r.value;
    }
    const arr = Object.values(byDate).sort((a:any,b:any) => a.date.localeCompare(b.date));
    if (!cumulative) return arr;
    const sums: Record<string, number> = {};
    for (const item of arr) {
      for (const m of selectedMetrics) {
        const val = item[m] ?? 0;
        sums[m] = (sums[m] ?? 0) + val;
        item[m] = sums[m];
      }
    }
    return arr;
  }, [rows, selectedMetrics, cumulative]);

  const [topTrips, setTopTrips] = useState<TopTrip[]>([]);
    const [topLoading, setTopLoading] = useState(true);
    const [topErr, setTopErr] = useState<string | null>(null);

    useEffect(() => {
    setTopLoading(true);
    setTopErr(null);
    (async () => {
        try {
        const data = await fetchAdminTopTrips({ start, end, limit: 8 });
        setTopTrips(data);
        } catch (e: any) {
        setTopErr(e.message ?? String(e));
        } finally {
        setTopLoading(false);
        }
    })();
    }, [start, end]);


  return (
    <div className="max-w-6xl mx-auto mt-10 p-6 space-y-6">
      <h1 className="text-2xl font-bold text-sky-400">📈 Engagement & KPIs</h1>

      {/* ── Svevana Credits KPIs ── */}
      <div className="bg-[#161b27] rounded-2xl border border-[#2a3347] p-4 space-y-3">
        <h2 className="font-semibold text-slate-300">✨ Svevana Credits</h2>
        {creditLoading ? (
          <p className="text-sm text-slate-500">Lade Credit-Statistiken…</p>
        ) : !creditStats ? (
          <p className="text-sm text-red-400">Fehler beim Laden.</p>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              <StatCard label="Guthaben gesamt" value={`${creditStats.total_balance} SC`} sub="aktuell ausstehend" color="indigo" />
              <StatCard label="Nutzer mit Guthaben" value={String(creditStats.users_with_balance)} sub="aktive Balance > 0" color="emerald" />
              <StatCard label="Gesamt gekauft" value={`${creditStats.total_purchased_sc} SC`} sub="alle Zeit" color="sky" />
              <StatCard label="Gesamt verbraucht" value={`${creditStats.total_consumed_sc} SC`} sub={`${creditStats.total_plannings} Planungen`} color="amber" />
              <StatCard label="Rückbuchungen" value={`${creditStats.total_refunded_sc} SC`} sub="Fehler-Refunds" color="red" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-1 border-t border-[#2a3347]">
              <StatCard label="Gekauft (7 Tage)" value={`${creditStats.purchases_last_7d} SC`} sub="" color="sky" />
              <StatCard label="Gekauft (30 Tage)" value={`${creditStats.purchases_last_30d} SC`} sub="" color="sky" />
              <StatCard label="Planungen (7 Tage)" value={String(creditStats.plannings_last_7d)} sub="" color="amber" />
              <StatCard label="Planungen (30 Tage)" value={String(creditStats.plannings_last_30d)} sub="" color="amber" />
            </div>
          </>
        )}
      </div>

      <div className="grid gap-3 md:grid-cols-4 bg-[#161b27] rounded-2xl border border-[#2a3347] p-4">
        <DateField label="Von" value={start} onChange={setStart} />
        <DateField label="Bis" value={end} onChange={setEnd} />
        <div className="flex flex-col">
          <label className="text-sm text-slate-400 mb-1">Bucket</label>
          <select value={bucket} onChange={(e)=>setBucket(e.target.value as any)} className="border border-[#2a3347] rounded px-2 py-1 bg-[#0d1117] text-slate-200 outline-none focus:border-sky-400">
            <option value="day">täglich</option>
            <option value="week">wöchentlich</option>
            <option value="month">monatlich</option>
          </select>
        </div>
        <div className="flex items-end gap-2">
          <QuickBtn onClick={()=>{setStart(isoDateNDaysAgo(7)); setEnd(isoDateNDaysAgo(0)); setBucket("day");}}>Letzte 7 Tage</QuickBtn>
          <QuickBtn onClick={()=>{setStart(isoDateNDaysAgo(30)); setEnd(isoDateNDaysAgo(0)); setBucket("day");}}>Letzte 30 Tage</QuickBtn>
          <label className="ml-auto inline-flex items-center gap-2 text-sm text-slate-300">
            <input type="checkbox" className="accent-sky-500" checked={cumulative} onChange={(e)=>setCumulative(e.target.checked)} />
            kumulativ
          </label>
        </div>
      </div>

      <MetricPicker selected={selectedMetrics} onChange={setSelectedMetrics} />

      <div className="w-full h-96 bg-[#161b27] rounded-2xl border border-[#2a3347] p-2">
        {loading ? (
          <div className="p-4 text-slate-400">Lade Daten…</div>
        ) : err ? (
          <div className="p-4 text-red-400">Fehler: {err}</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a3347" />
              <XAxis dataKey="date" stroke="#64748b" tick={{ fill: "#94a3b8" }} />
              <YAxis allowDecimals={false} stroke="#64748b" tick={{ fill: "#94a3b8" }} />
              <Tooltip contentStyle={{ backgroundColor: "#161b27", border: "1px solid #2a3347", color: "#e2e8f0" }} />
              <Legend wrapperStyle={{ color: "#94a3b8" }} />
              {selectedMetrics.map((m, i) => (
                <Line key={m} type="monotone" dataKey={m} name={KPI_LABELS[m] ?? m} stroke={COLORS[i % COLORS.length]} dot={false} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="bg-[#161b27] rounded-2xl border border-[#2a3347] p-4">
    <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-slate-100">🔥 Aktivste Trips ({start} → {end})</h3>
        <span className="text-xs text-slate-500">
        Score aus Programmpunkten, Dokumenten, Ausgaben, Zahlungen, Teilnehmern, Todos, Quiz-Fragen, Vorschlägen
        </span>
    </div>

    {topLoading ? (
        <div className="p-2 text-slate-400">Lade Top-Trips…</div>
    ) : topErr ? (
        <div className="p-2 text-red-400">Fehler: {topErr}</div>
    ) : topTrips.length === 0 ? (
        <div className="p-2 text-slate-500">Keine Aktivitäten im Zeitraum.</div>
    ) : (
        <ul className="divide-y divide-[#2a3347]">
        {topTrips.map((t, i) => (
            <li key={t.trip_id} className="py-3 flex items-center gap-4">
            <div className="w-6 text-right text-sm text-slate-500">{i + 1}.</div>

            <div className="flex-1">
                <Link to={`/trip/${t.trip_id}`} className="font-medium text-slate-100 hover:underline">
                {t.trip_name}
                </Link>
                <div className="text-xs text-slate-500 mt-0.5">
                {renderBreakdown(t.breakdown)}
                </div>
            </div>

            <div className="shrink-0">
                <span className="inline-flex items-center px-2 py-1 rounded-full text-sm bg-sky-900/40 text-sky-400 border border-sky-500/30">
                Score: {Number(t.score).toFixed(1)}
                </span>
            </div>
            </li>
        ))}
        </ul>
    )}
    </div>

    </div>
  );
}

function DateField({ label, value, onChange }: { label:string; value:string; onChange:(v:string)=>void }) {
  return (
    <div className="flex flex-col">
      <label className="text-sm text-slate-400 mb-1">{label}</label>
      <input type="date" value={value} onChange={(e)=>onChange(e.target.value)} className="border border-[#2a3347] rounded px-2 py-1 bg-[#0d1117] text-slate-200 outline-none focus:border-sky-400" />
    </div>
  );
}
function QuickBtn({ children, onClick }:{children:React.ReactNode; onClick:()=>void}) {
  return <button className="px-3 py-2 rounded bg-[#1e2639] hover:bg-[#2a3347] text-slate-300 text-sm transition-colors" onClick={onClick}>{children}</button>;
}
function isoDateNDaysAgo(n: number) {
  const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0,10);
}
function MetricPicker({ selected, onChange }: { selected: string[]; onChange: (m: string[]) => void; }) {
  const all = Object.keys(KPI_LABELS);
  return (
    <div className="bg-[#161b27] rounded-2xl border border-[#2a3347] p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-slate-100">Metriken</h3>
        <div className="flex gap-2">
          <button className="px-2 py-1 text-xs rounded bg-[#1e2639] hover:bg-[#2a3347] text-slate-300 transition-colors" onClick={()=>onChange(all)}>alle</button>
          <button className="px-2 py-1 text-xs rounded bg-[#1e2639] hover:bg-[#2a3347] text-slate-300 transition-colors" onClick={()=>onChange([...DEFAULT_SELECTED])}>Preset</button>
          <button className="px-2 py-1 text-xs rounded bg-[#1e2639] hover:bg-[#2a3347] text-slate-300 transition-colors" onClick={()=>onChange([])}>keine</button>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
        {all.map((m) => {
          const active = selected.includes(m);
          return (
            <button
              key={m}
              className={`text-left px-3 py-2 rounded border text-sm transition-colors ${active ? "bg-sky-900/40 border-sky-500/30 text-sky-400" : "bg-[#1e2639] border-[#2a3347] text-slate-300 hover:border-sky-500/20"}`}
              onClick={() => active ? onChange(selected.filter(s => s !== m)) : onChange([...selected, m])}
              title={m}
            >
              <span className="font-medium">{KPI_LABELS[m] ?? m}</span>
              <span className="ml-2 text-xs text-slate-500">({m})</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, color }: { label: string; value: string; sub: string; color: "indigo"|"emerald"|"sky"|"amber"|"red" }) {
  const colorMap = {
    indigo: "bg-sky-900/20 border-sky-500/20 text-sky-400",
    emerald: "bg-emerald-900/20 border-emerald-500/20 text-emerald-400",
    sky: "bg-sky-900/20 border-sky-500/20 text-sky-400",
    amber: "bg-amber-500/10 border-amber-500/20 text-amber-400",
    red: "bg-red-900/20 border-red-500/20 text-red-400",
  };
  return (
    <div className={`rounded-xl border px-3 py-2 ${colorMap[color]}`}>
      <p className="text-xs text-slate-500 mb-0.5">{label}</p>
      <p className="text-xl font-bold">{value}</p>
      {sub && <p className="text-xs opacity-60 mt-0.5">{sub}</p>}
    </div>
  );
}

function renderBreakdown(bd: TopTrip["breakdown"]) {
  const parts: string[] = [];
  if (bd.program_items) parts.push(`🗓️ ${bd.program_items} PI`);
  if (bd.documents)     parts.push(`📄 ${bd.documents} Doks`);
  if (bd.expenses)      parts.push(`💸 ${bd.expenses} Ausg.`);
  if (bd.payments)      parts.push(`💳 ${bd.payments} Zahl.`);
  if (bd.participants)  parts.push(`👥 ${bd.participants} Teiln.`);
  if (bd.todos)         parts.push(`☑️ ${bd.todos} Todos`);
  if (bd.quiz_q)        parts.push(`❓ ${bd.quiz_q} Quiz-F`);
  if (bd.suggestions)   parts.push(`💡 ${bd.suggestions} Vorschl.`);
  return parts.slice(0, 6).join(" · ");
}
